// src/pages/PrivacyPolicy.jsx - Fixed icon positioning for mobile

import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FiShield,
  FiLock,
  FiEye,
  FiUser,
  FiMail,
  FiDatabase,
  FiServer,
  FiGlobe,
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiFileText,
  FiShare2,
  FiTrash2,
  FiRefreshCw,
  FiSearch,
  FiCpu,
  FiCode,
  FiSmartphone,
  FiMonitor,
  FiDownload,
  FiUpload,
  FiSettings,
  FiZap,
  FiHeart,
  FiStar,
  FiAward,
  FiUsers,
  FiBriefcase,
  FiCalendar,
  FiMessageCircle,
  FiThumbsUp,
  FiTrendingUp,
  FiArrowRight,
  FiBookOpen,
  FiInfo,
  FiLayers,
  FiGrid,
  FiPackage,
  FiPenTool,
  FiChevronDown,
  FiDollarSign,
  FiAlertTriangle,
  FiInfo as FiInfoIcon,
  FiExternalLink,
} from 'react-icons/fi';
import { useTheme } from '../contexts/ThemeContext';

// =============================================
// GLASSY CARD
// =============================================
const GlassyCard = ({ children, className = '' }) => {
  return (
    <div
      className={`relative overflow-hidden backdrop-blur-xl ${className}`}
      style={{
        backgroundColor: 'rgba(var(--bg-rgb), 0.3)',
        border: '1px solid rgba(var(--text-rgb), 0.06)',
        borderRadius: '16px',
        boxShadow:
          '0 4px 24px rgba(0, 0, 0, 0.02), inset 0 1px 0 rgba(255, 255, 255, 0.03)',
      }}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-white/3 via-transparent to-transparent" />
        <div className="absolute -inset-full w-1/2 rotate-12 bg-gradient-to-r from-transparent via-white/5 to-transparent transform -translate-x-full animate-[shimmer_8s_infinite]" />
      </div>
      {children}
    </div>
  );
};

// =============================================
// ANIMATION VARIANTS
// =============================================
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 },
  },
};

// =============================================
// POLICY SECTION - FIXED ICON POSITIONING
// =============================================
const PolicySection = ({ id, title, icon: Icon, children, lastUpdated }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <motion.div id={id} variants={fadeInUp} className="scroll-mt-40">
      <GlassyCard className="mb-6 p-4 sm:p-6 rounded-2xl transition-all duration-300 hover:shadow-xl">
        <div
          className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {/* Icon - Centered on mobile, left on desktop */}
          <div className="flex sm:block items-center justify-center w-full sm:w-auto">
            <div
              className="p-2 sm:p-3 rounded-xl flex-shrink-0 transition-all duration-300 group-hover:scale-110 inline-flex"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <Icon
                className="w-4 h-4 sm:w-5 sm:h-5"
                style={{ color: 'var(--text)' }}
              />
            </div>
          </div>

          {/* Content - Takes full width */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h3
                className="text-base sm:text-xl font-semibold truncate"
                style={{ color: 'var(--text)' }}
              >
                {title}
              </h3>
              <motion.div
                animate={{ rotate: isExpanded ? 180 : 0 }}
                transition={{ duration: 0.3 }}
                className="text-sm flex-shrink-0"
                style={{ color: 'var(--muted)' }}
              >
                <FiChevronDown className="w-4 h-4 sm:w-5 sm:h-5" />
              </motion.div>
            </div>

            {/* Content - Full width, no padding issues */}
            <div
              className="text-sm leading-relaxed mt-3 space-y-3 w-full overflow-hidden"
              style={{ color: 'var(--muted)' }}
            >
              {children}
            </div>

            {lastUpdated && (
              <div
                className="mt-4 pt-3 border-t text-xs"
                style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}
              >
                Last updated: {lastUpdated}
              </div>
            )}
          </div>
        </div>
      </GlassyCard>
    </motion.div>
  );
};

// =============================================
// QUICK NAVIGATION - FIXED STICKY
// =============================================
const QuickNav = ({ sections }) => {
  const [activeSection, setActiveSection] = useState('');
  const navRef = useRef(null);

  const handleNavClick = (e, sectionId) => {
    e.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 200;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
    setActiveSection(sectionId);
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;
      let currentSection = '';

      sections.forEach(section => {
        const element = document.getElementById(section.id);
        if (element) {
          const offsetTop = element.offsetTop;
          if (scrollPosition >= offsetTop) {
            currentSection = section.id;
          }
        }
      });

      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  return (
    <div ref={navRef} className="sticky z-40 mb-8" style={{ top: '80px' }}>
      <GlassyCard className="p-3 rounded-xl">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className="text-xs font-medium mr-1 flex items-center gap-1.5"
            style={{ color: 'var(--muted)' }}
          >
            <FiLayers className="w-3.5 h-3.5" />
            <span className="hidden xs:inline">Jump to:</span>
          </span>
          <div className="flex flex-wrap gap-1.5">
            {sections.map((section, index) => (
              <a
                key={index}
                href={`#${section.id}`}
                onClick={e => handleNavClick(e, section.id)}
                className={`text-[10px] px-2.5 py-1 rounded-full transition-all duration-200 hover:scale-105 cursor-pointer whitespace-nowrap ${
                  activeSection === section.id ? 'ring-2' : ''
                }`}
                style={{
                  backgroundColor:
                    activeSection === section.id
                      ? 'var(--text)'
                      : 'var(--border)',
                  color:
                    activeSection === section.id ? 'var(--bg)' : 'var(--text)',
                  ringColor: 'var(--text)',
                }}
              >
                {section.label}
              </a>
            ))}
          </div>
        </div>
      </GlassyCard>
    </div>
  );
};

// =============================================
// RIGHT SIDEBAR - FIXED STICKY
// =============================================
const RightSidebar = () => {
  const quickLinks = [
    { icon: FiShield, label: 'Data Protection', href: '#information-collect' },
    { icon: FiLock, label: 'Security Measures', href: '#data-security' },
    { icon: FiUser, label: 'Your Rights', href: '#your-rights' },
    { icon: FiMail, label: 'Email Guidelines', href: '#email-guidelines' },
    { icon: FiMessageCircle, label: 'Contact Us', href: '#contact' },
  ];

  const designResources = [
    { icon: FiPenTool, label: 'Design Tips', href: '/blog' },
    { icon: FiPackage, label: 'Services', href: '/services' },
    { icon: FiGrid, label: 'Portfolio', href: '/portfolio' },
    { icon: FiBookOpen, label: 'Blog', href: '/blog' },
  ];

  const handleSidebarClick = (e, href) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      const id = href.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        const offset = 200;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Google Ads Space */}
      <GlassyCard className="p-6 rounded-2xl text-center">
        <div
          className="border border-dashed rounded-xl p-8"
          style={{ borderColor: 'var(--border)' }}
        >
          <div className="flex flex-col items-center gap-3">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <FiGlobe className="w-6 h-6" style={{ color: 'var(--muted)' }} />
            </div>
            <span className="text-xs" style={{ color: 'var(--muted)' }}>
              📢 Google Ad Space
            </span>
            <div
              className="w-full h-16 rounded-lg"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <div
                className="flex items-center justify-center h-full text-xs"
                style={{ color: 'var(--muted)' }}
              >
                Ad Placement
              </div>
            </div>
          </div>
        </div>
      </GlassyCard>

      {/* Quick Links */}
      <GlassyCard className="p-6 rounded-2xl">
        <h4
          className="text-sm font-semibold mb-4 flex items-center gap-2"
          style={{ color: 'var(--text)' }}
        >
          <FiZap className="w-4 h-4" style={{ color: '#f59e0b' }} />
          Quick Links
        </h4>
        <ul className="space-y-2">
          {quickLinks.map((link, idx) => (
            <motion.li key={idx} whileHover={{ x: 4 }} className="group">
              <a
                href={link.href}
                onClick={e => handleSidebarClick(e, link.href)}
                className="flex items-center gap-3 text-sm transition-colors duration-200"
                style={{ color: 'var(--muted)' }}
              >
                <link.icon
                  className="w-4 h-4 flex-shrink-0"
                  style={{ color: 'var(--text)' }}
                />
                <span className="group-hover:text-[var(--text)]">
                  {link.label}
                </span>
                <FiArrowRight className="w-3 h-3 ml-auto opacity-0 group-hover:opacity-100 transition-all duration-200" />
              </a>
            </motion.li>
          ))}
        </ul>
      </GlassyCard>

      {/* Design Resources */}
      <GlassyCard className="p-6 rounded-2xl">
        <h4
          className="text-sm font-semibold mb-4 flex items-center gap-2"
          style={{ color: 'var(--text)' }}
        >
          <FiStar className="w-4 h-4" style={{ color: '#f59e0b' }} />
          Design Resources
        </h4>
        <ul className="space-y-2">
          {designResources.map((resource, idx) => (
            <motion.li key={idx} whileHover={{ x: 4 }} className="group">
              <Link
                to={resource.href}
                className="flex items-center gap-3 text-sm transition-colors duration-200"
                style={{ color: 'var(--muted)' }}
              >
                <resource.icon
                  className="w-4 h-4 flex-shrink-0"
                  style={{ color: 'var(--text)' }}
                />
                <span className="group-hover:text-[var(--text)]">
                  {resource.label}
                </span>
                <FiArrowRight className="w-3 h-3 ml-auto opacity-0 group-hover:opacity-100 transition-all duration-200" />
              </Link>
            </motion.li>
          ))}
        </ul>
      </GlassyCard>

      {/* Trust Badges */}
      <GlassyCard className="p-6 rounded-2xl text-center">
        <div className="flex justify-center gap-4 mb-3">
          <div className="flex items-center gap-2">
            <FiShield className="w-5 h-5 text-green-500" />
            <span
              className="text-xs font-medium"
              style={{ color: 'var(--text)' }}
            >
              GDPR Compliant
            </span>
          </div>
          <div className="flex items-center gap-2">
            <FiLock className="w-5 h-5 text-blue-500" />
            <span
              className="text-xs font-medium"
              style={{ color: 'var(--text)' }}
            >
              SSL Secure
            </span>
          </div>
        </div>
        <p className="text-xs" style={{ color: 'var(--muted)' }}>
          Your data is safe with us. We follow industry best practices.
        </p>
      </GlassyCard>

      {/* Google Ads Space - Bottom */}
      <GlassyCard className="p-6 rounded-2xl text-center">
        <div
          className="border border-dashed rounded-xl p-6"
          style={{ borderColor: 'var(--border)' }}
        >
          <span className="text-xs" style={{ color: 'var(--muted)' }}>
            📢 Google Ad Space
          </span>
          <div
            className="w-full h-12 mt-2 rounded-lg"
            style={{ backgroundColor: 'var(--border)' }}
          >
            <div
              className="flex items-center justify-center h-full text-xs"
              style={{ color: 'var(--muted)' }}
            >
              Ad Placement
            </div>
          </div>
        </div>
      </GlassyCard>
    </div>
  );
};

// =============================================
// HERO SECTION
// =============================================
const PrivacyHero = () => {
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="relative mb-8 overflow-hidden rounded-3xl"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 dark:from-blue-500/10 dark:to-purple-500/10" />

      <motion.div
        animate={{
          x: ['-10%', '10%', '-10%'],
          y: ['-10%', '15%', '-10%'],
          scale: [1, 1.2, 1],
        }}
        transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute -top-20 -left-20 w-72 h-72 rounded-full blur-3xl"
        style={{
          background: 'radial-gradient(circle, #3b82f6 0%, transparent 70%)',
          opacity: 0.06,
        }}
      />
      <motion.div
        animate={{
          x: ['15%', '-10%', '15%'],
          y: ['15%', '-10%', '15%'],
          scale: [1.2, 1, 1.2],
        }}
        transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute -bottom-20 -right-20 w-80 h-80 rounded-full blur-3xl"
        style={{
          background: 'radial-gradient(circle, #8b5cf6 0%, transparent 70%)',
          opacity: 0.04,
        }}
      />

      <div className="relative px-4 sm:px-6 py-10 md:py-14 text-center">
        <motion.div
          variants={fadeInUp}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4 text-sm backdrop-blur-sm"
          style={{
            backgroundColor: 'rgba(var(--bg-rgb), 0.5)',
            border: '1px solid rgba(var(--text-rgb), 0.06)',
          }}
        >
          <FiShield className="w-4 h-4" style={{ color: '#3b82f6' }} />
          <span style={{ color: 'var(--muted)' }}>Privacy & Security</span>
        </motion.div>

        <motion.h1
          variants={fadeInUp}
          className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 tracking-tight"
          style={{ color: 'var(--text)' }}
        >
          Privacy{' '}
          <span className="italic" style={{ color: 'var(--muted)' }}>
            Policy
          </span>
        </motion.h1>

        <motion.p
          variants={fadeInUp}
          className="text-sm sm:text-base max-w-2xl mx-auto"
          style={{ color: 'var(--muted)', opacity: 0.8 }}
        >
          Your privacy matters to us. Learn how we collect, use, and protect
          your personal information.
        </motion.p>

        <motion.div
          variants={fadeInUp}
          className="flex flex-wrap justify-center gap-2 sm:gap-3 mt-4"
        >
          <div
            className="flex items-center gap-2 px-3 sm:px-4 py-1.5 rounded-full text-[10px] sm:text-xs backdrop-blur-sm"
            style={{
              backgroundColor: 'rgba(var(--bg-rgb), 0.3)',
              border: '1px solid rgba(var(--text-rgb), 0.06)',
            }}
          >
            <FiClock className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            <span style={{ color: 'var(--text)' }}>
              Last Updated: January 2025
            </span>
          </div>
          <div
            className="flex items-center gap-2 px-3 sm:px-4 py-1.5 rounded-full text-[10px] sm:text-xs backdrop-blur-sm"
            style={{
              backgroundColor: 'rgba(var(--bg-rgb), 0.3)',
              border: '1px solid rgba(var(--text-rgb), 0.06)',
            }}
          >
            <FiCheckCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-green-500" />
            <span style={{ color: 'var(--text)' }}>GDPR Compliant</span>
          </div>
        </motion.div>

        {/* Decorative line */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="mt-6 flex items-center justify-center gap-3"
        >
          <span
            className="w-8 sm:w-12 h-px"
            style={{ backgroundColor: 'var(--border)' }}
          />
          <span
            className="text-[10px] sm:text-xs tracking-widest uppercase"
            style={{ color: 'var(--muted)', opacity: 0.3 }}
          >
            Trust & Transparency
          </span>
          <span
            className="w-8 sm:w-12 h-px"
            style={{ backgroundColor: 'var(--border)' }}
          />
        </motion.div>
      </div>
    </motion.div>
  );
};

// =============================================
// MAIN COMPONENT
// =============================================
const PrivacyPolicy = () => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  useEffect(() => {
    window.scrollTo(0, 0);

    if (window.location.hash) {
      setTimeout(() => {
        const id = window.location.hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          const offset = 200;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          });
        }
      }, 500);
    }
  }, []);

  const sections = [
    { id: 'introduction', label: 'Introduction' },
    { id: 'email-guidelines', label: 'Email Guidelines' },
    { id: 'information-collect', label: 'Information We Collect' },
    { id: 'how-we-use', label: 'How We Use Information' },
    { id: 'data-security', label: 'Data Security' },
    { id: 'cookies', label: 'Cookies' },
    { id: 'third-party', label: 'Third-Party Services' },
    { id: 'your-rights', label: 'Your Rights' },
    { id: 'contact', label: 'Contact Us' },
  ];

  return (
    <>
      <Helmet>
        <title>Privacy Policy | WD Hajbul - Graphic Designer</title>
        <meta
          name="description"
          content="Learn how WD Hajbul collects, uses, and protects your personal information. Your privacy and security are our top priorities as a professional graphic designer."
        />
        <meta
          name="keywords"
          content="privacy policy, data protection, GDPR, personal information, privacy, security, graphic designer privacy, design studio privacy, email response, spam folder"
        />
        <link rel="canonical" href="https://wdhejbul.com/privacy-policy" />
        <meta name="robots" content="index, follow" />

        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Privacy Policy',
            description:
              'Learn how WD Hajbul collects, uses, and protects your personal information.',
            url: 'https://wdhejbul.com/privacy-policy',
            about: {
              '@type': 'Thing',
              name: 'Privacy Policy',
            },
          })}
        </script>
      </Helmet>

      <div
        className="min-h-screen pt-32 pb-20 max-sm:pt-24"
        style={{ backgroundColor: 'var(--bg)', color: 'var(--text)' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Main Content - 8 columns */}
            <div className="lg:col-span-8">
              {/* Hero Section */}
              <PrivacyHero />

              {/* Quick Navigation - Sticky */}
              <QuickNav sections={sections} />

              {/* Policy Content */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={staggerContainer}
                className="space-y-4"
              >
                {/* Introduction */}
                <PolicySection
                  id="introduction"
                  title="Introduction"
                  icon={FiShield}
                  lastUpdated="January 2025"
                >
                  <p>
                    At WD Hajbul, we are committed to protecting your privacy
                    and ensuring the security of your personal information. This
                    Privacy Policy explains how we collect, use, disclose, and
                    safeguard your information when you visit our website or use
                    our graphic design services.
                  </p>
                  <p>
                    As a professional graphic designer, I understand the
                    importance of trust and confidentiality. This policy
                    reflects our dedication to transparency and data protection
                    in the creative industry.
                  </p>
                  <div
                    className="mt-3 p-4 rounded-lg"
                    style={{ backgroundColor: 'var(--border)' }}
                  >
                    <div className="flex items-start gap-3">
                      <FiInfoIcon
                        className="w-5 h-5 mt-0.5 flex-shrink-0"
                        style={{ color: '#3b82f6' }}
                      />
                      <div>
                        <p
                          className="text-sm font-medium"
                          style={{ color: 'var(--text)' }}
                        >
                          Important Notice
                        </p>
                        <p
                          className="text-xs"
                          style={{ color: 'var(--muted)' }}
                        >
                          Please provide accurate and valid information when
                          contacting us. Invalid or fake information may result
                          in:
                        </p>
                        <ul
                          className="text-xs list-disc list-inside mt-1 space-y-0.5"
                          style={{ color: 'var(--muted)' }}
                        >
                          <li>
                            Inability to receive email notifications when we
                            respond
                          </li>
                          <li>
                            Delayed or missed communication about your projects
                          </li>
                          <li>Your inquiry may be ignored or marked as spam</li>
                          <li>
                            Inability to verify your identity for security
                            purposes
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </PolicySection>

                {/* Email Guidelines */}
                <PolicySection
                  id="email-guidelines"
                  title="Email Communication & Response Guidelines"
                  icon={FiMail}
                  lastUpdated="January 2025"
                >
                  <div className="space-y-4">
                    <div
                      className="p-4 rounded-lg border-2"
                      style={{
                        backgroundColor: 'rgba(245, 158, 11, 0.08)',
                        borderColor: 'rgba(245, 158, 11, 0.3)',
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <FiAlertTriangle
                          className="w-5 h-5 mt-0.5 flex-shrink-0"
                          style={{ color: '#f59e0b' }}
                        />
                        <div>
                          <p
                            className="text-sm font-bold"
                            style={{ color: '#f59e0b' }}
                          >
                            📬 Important: Check Your Spam Folder
                          </p>
                          <p
                            className="text-xs mt-1"
                            style={{ color: 'var(--muted)' }}
                          >
                            When you submit an inquiry or contact us through our
                            forms, we will respond via email within 24 hours.
                            However, please be aware that:
                          </p>
                          <ul
                            className="text-xs list-disc list-inside mt-2 space-y-1"
                            style={{ color: 'var(--muted)' }}
                          >
                            <li>
                              <strong>Emails may go to spam:</strong> Our
                              responses may be filtered into your spam/junk
                              folder by your email provider (Gmail, Outlook,
                              Yahoo, etc.).
                            </li>
                            <li>
                              <strong>Why this happens:</strong> We use a
                              free/standard email service (Nodemailer/Gmail)
                              without a custom domain, which can trigger spam
                              filters.
                            </li>
                            <li>
                              <strong>What you should do:</strong> Check your
                              spam folder if you don't see our reply within 24
                              hours.
                            </li>
                            <li>
                              <strong>Add to contacts:</strong> Add{' '}
                              <span className="font-mono text-blue-500">
                                wdhejbul@gmail.com
                              </span>{' '}
                              to your contacts to ensure our emails go to your
                              inbox.
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div
                      className="p-4 rounded-lg"
                      style={{
                        backgroundColor: 'rgba(59, 130, 246, 0.06)',
                        border: '1px solid rgba(59, 130, 246, 0.15)',
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <FiMail
                          className="w-5 h-5 mt-0.5 flex-shrink-0"
                          style={{ color: '#3b82f6' }}
                        />
                        <div>
                          <p
                            className="text-sm font-medium"
                            style={{ color: '#3b82f6' }}
                          >
                            💡 Pro Tip: How to Ensure You Receive Our Responses
                          </p>
                          <ul
                            className="text-xs list-disc list-inside mt-2 space-y-1"
                            style={{ color: 'var(--muted)' }}
                          >
                            <li>
                              <strong>Add to contacts:</strong> Add{' '}
                              <span className="font-mono text-blue-500">
                                wdhejbul@gmail.com
                              </span>{' '}
                              to your email contacts. This tells your email
                              provider (Gmail, Outlook, Yahoo, etc.) that we're
                              a trusted sender.
                            </li>
                            <li>
                              <strong>Mark as "Not Spam":</strong> If you find
                              our email in spam, mark it as "Not Spam" to train
                              your email filter.
                            </li>
                            <li>
                              <strong>Alternative contact:</strong> Provide your
                              phone number or WhatsApp in your message so we can
                              reach you directly if email delivery fails.
                            </li>
                            <li>
                              <strong>Check regularly:</strong> If you're
                              expecting a response, check your spam folder
                              regularly for 48 hours after submitting your
                              inquiry.
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div
                      className="p-4 rounded-lg"
                      style={{ backgroundColor: 'var(--border)' }}
                    >
                      <div className="flex items-start gap-3">
                        <FiInfo
                          className="w-5 h-5 mt-0.5 flex-shrink-0"
                          style={{ color: 'var(--text)' }}
                        />
                        <div>
                          <p
                            className="text-sm font-medium"
                            style={{ color: 'var(--text)' }}
                          >
                            Why Do Our Emails Go to Spam?
                          </p>
                          <p
                            className="text-xs mt-1"
                            style={{ color: 'var(--muted)' }}
                          >
                            Email providers use spam filters to protect users
                            from unwanted emails. Here are the common reasons
                            why our emails might be flagged:
                          </p>
                          <ul
                            className="text-xs list-disc list-inside mt-2 space-y-1"
                            style={{ color: 'var(--muted)' }}
                          >
                            <li>
                              <strong>No Custom Domain:</strong> We use a
                              standard email service (Gmail) rather than a
                              custom domain email (e.g., hello@wdhejbul.com).
                              Emails from generic domains are more likely to be
                              filtered.
                            </li>
                            <li>
                              <strong>Free Email Service:</strong> Nodemailer
                              with Gmail is a free service that can trigger spam
                              filters on some providers.
                            </li>
                            <li>
                              <strong>Sender Reputation:</strong> New senders or
                              senders with low activity can be flagged
                              initially.
                            </li>
                            <li>
                              <strong>Email Content:</strong> Certain words or
                              links in emails can trigger spam filters.
                            </li>
                          </ul>
                          <p
                            className="text-xs mt-2"
                            style={{ color: 'var(--muted)' }}
                          >
                            <span className="font-medium">
                              What We're Doing About It:
                            </span>{' '}
                            We're working on moving to a custom domain email
                            service to improve deliverability. In the meantime,
                            we appreciate your understanding and cooperation.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div
                      className="p-4 rounded-lg"
                      style={{
                        backgroundColor: 'rgba(16, 185, 129, 0.06)',
                        border: '1px solid rgba(16, 185, 129, 0.15)',
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <FiMessageCircle
                          className="w-5 h-5 mt-0.5 flex-shrink-0"
                          style={{ color: '#10b981' }}
                        />
                        <div>
                          <p
                            className="text-sm font-medium"
                            style={{ color: '#10b981' }}
                          >
                            📱 Alternative Contact Methods
                          </p>
                          <p
                            className="text-xs mt-1"
                            style={{ color: 'var(--muted)' }}
                          >
                            If you prefer not to rely on email or have concerns
                            about spam filters, you can reach us through:
                          </p>
                          <ul
                            className="text-xs list-disc list-inside mt-2 space-y-1"
                            style={{ color: 'var(--muted)' }}
                          >
                            <li>
                              <strong>Contact Form:</strong> Use our contact
                              form and provide your phone number for direct
                              follow-up.
                            </li>
                            <li>
                              <strong>Freelance Platforms:</strong> Connect
                              through Fiverr or Upwork where messages are
                              handled within their secure platforms.
                            </li>
                            <li>
                              <strong>Social Media:</strong> Reach out via
                              Instagram or LinkedIn for quick responses.
                            </li>
                            <li>
                              <strong>Phone/WhatsApp:</strong> If you provide
                              your phone number in your message, we can contact
                              you directly.
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </PolicySection>

                {/* Information We Collect */}
                <PolicySection
                  id="information-collect"
                  title="Information We Collect"
                  icon={FiDatabase}
                >
                  <div className="space-y-4">
                    <div>
                      <h4
                        className="font-semibold mb-2"
                        style={{ color: 'var(--text)' }}
                      >
                        Personal Information You Provide
                      </h4>
                      <ul className="list-disc list-inside space-y-1">
                        <li>
                          Name and contact information (email, phone number)
                        </li>
                        <li>Company name and job title</li>
                        <li>
                          Project briefs, design requirements, and creative
                          briefs
                        </li>
                        <li>Communication history and feedback</li>
                        <li>Portfolio and design asset submissions</li>
                        <li>
                          Social media profiles and online presence (for
                          collaboration)
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h4
                        className="font-semibold mb-2"
                        style={{ color: 'var(--text)' }}
                      >
                        Automatically Collected Information
                      </h4>
                      <ul className="list-disc list-inside space-y-1">
                        <li>IP address and browser type</li>
                        <li>Device information and operating system</li>
                        <li>Pages visited and time spent on our website</li>
                        <li>Referral sources and search queries</li>
                        <li>Cookies and similar tracking technologies</li>
                      </ul>
                    </div>
                  </div>
                </PolicySection>

                {/* How We Use Information */}
                <PolicySection
                  id="how-we-use"
                  title="How We Use Your Information"
                  icon={FiCpu}
                >
                  <div className="space-y-3">
                    <p>
                      We use the information we collect for the following
                      purposes:
                    </p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>
                        To provide and maintain our graphic design services
                      </li>
                      <li>To respond to your inquiries and project requests</li>
                      <li>To improve our website and client experience</li>
                      <li>
                        To send you relevant updates and design inspiration
                      </li>
                      <li>To analyze website usage and trends</li>
                      <li>
                        To ensure the security and integrity of our platform
                      </li>
                      <li>To comply with legal obligations and regulations</li>
                      <li>
                        To showcase your project in our portfolio (with your
                        consent)
                      </li>
                      <li>
                        To send you email notifications about your inquiries and
                        responses
                      </li>
                    </ul>
                    <div
                      className="mt-3 p-3 rounded-lg"
                      style={{ backgroundColor: 'var(--border)' }}
                    >
                      <div className="flex items-start gap-2">
                        <FiCheckCircle className="w-4 h-4 mt-0.5 text-green-500 flex-shrink-0" />
                        <p
                          className="text-xs"
                          style={{ color: 'var(--muted)' }}
                        >
                          <span
                            className="font-medium"
                            style={{ color: 'var(--text)' }}
                          >
                            Valid Information Required:
                          </span>{' '}
                          We rely on accurate contact information to send you
                          email notifications about our responses. Please ensure
                          your email address is correct to receive important
                          updates. Check your spam folder if you don't see our
                          reply.
                        </p>
                      </div>
                    </div>
                  </div>
                </PolicySection>

                {/* Data Security */}
                <PolicySection
                  id="data-security"
                  title="Data Security"
                  icon={FiLock}
                >
                  <div className="space-y-3">
                    <p>
                      We implement industry-standard security measures to
                      protect your personal information and design assets from
                      unauthorized access, disclosure, alteration, or
                      destruction.
                    </p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Encryption of data in transit (SSL/TLS)</li>
                      <li>Secure cloud storage for design files and assets</li>
                      <li>
                        Regular security audits and vulnerability assessments
                      </li>
                      <li>NDA agreements for sensitive client projects</li>
                      <li>Employee training on data protection and privacy</li>
                      <li>Secure client portals for file sharing</li>
                      <li>
                        Incident response and breach notification procedures
                      </li>
                    </ul>
                    <p className="mt-3">
                      We treat your design assets and personal information with
                      the same care we would expect for our own. Your creative
                      work is safe with us.
                    </p>
                  </div>
                </PolicySection>

                {/* Cookies */}
                <PolicySection
                  id="cookies"
                  title="Cookies & Tracking"
                  icon={FiRefreshCw}
                >
                  <div className="space-y-3">
                    <p>
                      We use cookies and similar tracking technologies to
                      enhance your experience on our website and improve our
                      design services.
                    </p>
                    <div>
                      <h4
                        className="font-semibold mb-2"
                        style={{ color: 'var(--text)' }}
                      >
                        Types of Cookies We Use
                      </h4>
                      <ul className="list-disc list-inside space-y-1">
                        <li>
                          <strong>Essential Cookies:</strong> Required for basic
                          website functionality
                        </li>
                        <li>
                          <strong>Analytics Cookies:</strong> Help us understand
                          how visitors interact with our portfolio
                        </li>
                        <li>
                          <strong>Preference Cookies:</strong> Remember your
                          settings and preferences
                        </li>
                        <li>
                          <strong>Marketing Cookies:</strong> Used to deliver
                          relevant design content
                        </li>
                      </ul>
                    </div>
                    <p className="mt-3">
                      You can control cookie preferences through your browser
                      settings. Disabling certain cookies may affect your
                      experience on our website.
                    </p>
                  </div>
                </PolicySection>

                {/* Third-Party Services */}
                <PolicySection
                  id="third-party"
                  title="Third-Party Services"
                  icon={FiGlobe}
                >
                  <div className="space-y-3">
                    <p>
                      We use third-party services to enhance our website and
                      provide better functionality for our clients.
                    </p>
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      <div
                        className="p-3 rounded-lg text-xs"
                        style={{ backgroundColor: 'var(--border)' }}
                      >
                        <span
                          className="font-medium block mb-1"
                          style={{ color: 'var(--text)' }}
                        >
                          Analytics
                        </span>
                        <span style={{ color: 'var(--muted)' }}>
                          Google Analytics, Hotjar
                        </span>
                      </div>
                      <div
                        className="p-3 rounded-lg text-xs"
                        style={{ backgroundColor: 'var(--border)' }}
                      >
                        <span
                          className="font-medium block mb-1"
                          style={{ color: 'var(--text)' }}
                        >
                          Hosting & Storage
                        </span>
                        <span style={{ color: 'var(--muted)' }}>
                          Firebase, Cloudinary
                        </span>
                      </div>
                      <div
                        className="p-3 rounded-lg text-xs"
                        style={{ backgroundColor: 'var(--border)' }}
                      >
                        <span
                          className="font-medium block mb-1"
                          style={{ color: 'var(--text)' }}
                        >
                          Email Services
                        </span>
                        <span style={{ color: 'var(--muted)' }}>
                          Nodemailer, Gmail
                        </span>
                      </div>
                      <div
                        className="p-3 rounded-lg text-xs"
                        style={{ backgroundColor: 'var(--border)' }}
                      >
                        <span
                          className="font-medium block mb-1"
                          style={{ color: 'var(--text)' }}
                        >
                          Freelance Platforms
                        </span>
                        <span style={{ color: 'var(--muted)' }}>
                          Fiverr, Upwork
                        </span>
                      </div>
                    </div>
                    <div
                      className="mt-3 p-3 rounded-lg"
                      style={{
                        backgroundColor: 'rgba(245, 158, 11, 0.06)',
                        border: '1px solid rgba(245, 158, 11, 0.15)',
                      }}
                    >
                      <div className="flex items-start gap-2">
                        <FiAlertTriangle className="w-4 h-4 mt-0.5 text-yellow-500 flex-shrink-0" />
                        <div>
                          <p
                            className="text-xs font-medium"
                            style={{ color: '#f59e0b' }}
                          >
                            Email Delivery Notice
                          </p>
                          <p
                            className="text-xs"
                            style={{ color: 'var(--muted)' }}
                          >
                            We use Nodemailer with Gmail to send email
                            responses. This free service may occasionally cause
                            our emails to be filtered as spam. Please check your
                            spam folder if you don't receive a response within
                            24 hours.
                          </p>
                        </div>
                      </div>
                    </div>
                    <p
                      className="mt-2 text-xs"
                      style={{ color: 'var(--muted)' }}
                    >
                      <strong>Note:</strong> We do not have an automated online
                      payment system on this website. All payments are handled
                      through direct collaboration or via freelance platforms
                      like Fiverr and Upwork.
                    </p>
                  </div>
                </PolicySection>

                {/* Payment Information */}
                <PolicySection
                  id="payment"
                  title="Payment & Billing"
                  icon={FiDollarSign}
                >
                  <div className="space-y-3">
                    <div
                      className="p-4 rounded-lg"
                      style={{ backgroundColor: 'var(--border)' }}
                    >
                      <div className="flex items-start gap-3">
                        <FiInfo
                          className="w-5 h-5 mt-0.5 flex-shrink-0"
                          style={{ color: '#f59e0b' }}
                        />
                        <div>
                          <p
                            className="text-sm font-medium"
                            style={{ color: 'var(--text)' }}
                          >
                            How We Handle Payments
                          </p>
                          <p
                            className="text-xs mt-1"
                            style={{ color: 'var(--muted)' }}
                          >
                            WD Hajbul does not have an automated online payment
                            system on this website. All financial transactions
                            are handled through:
                          </p>
                          <ul
                            className="text-xs list-disc list-inside mt-2 space-y-1"
                            style={{ color: 'var(--muted)' }}
                          >
                            <li>
                              <strong>Direct Collaboration:</strong> We work
                              directly with clients on a project-by-project
                              basis. Payments are agreed upon and processed
                              through direct communication and invoicing.
                            </li>
                            <li>
                              <strong>Freelance Platforms:</strong> For secure
                              transactions, we also work through trusted
                              platforms like <strong>Fiverr</strong> and{' '}
                              <strong>Upwork</strong>, where payments are
                              handled through their secure systems.
                            </li>
                          </ul>
                          <p
                            className="text-xs mt-2"
                            style={{ color: 'var(--muted)' }}
                          >
                            If you have any questions about payment, please
                            contact us directly.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </PolicySection>

                {/* Your Rights */}
                <PolicySection
                  id="your-rights"
                  title="Your Rights & Choices"
                  icon={FiUser}
                >
                  <div className="space-y-3">
                    <p>
                      You have certain rights regarding your personal
                      information under applicable data protection laws,
                      including the GDPR and CCPA.
                    </p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>
                        <strong>Right to Access:</strong> Request a copy of your
                        personal data
                      </li>
                      <li>
                        <strong>Right to Rectification:</strong> Correct
                        inaccurate or incomplete data
                      </li>
                      <li>
                        <strong>Right to Erasure:</strong> Request deletion of
                        your personal data
                      </li>
                      <li>
                        <strong>Right to Restrict Processing:</strong> Limit how
                        we use your data
                      </li>
                      <li>
                        <strong>Right to Data Portability:</strong> Receive your
                        data in a portable format
                      </li>
                      <li>
                        <strong>Right to Object:</strong> Object to certain
                        processing activities
                      </li>
                    </ul>
                    <p className="mt-3">
                      To exercise any of these rights, please contact us using
                      the information provided below. We will respond to your
                      request within 30 days.
                    </p>
                  </div>
                </PolicySection>

                {/* Contact Us */}
                <PolicySection
                  id="contact"
                  title="Contact Us"
                  icon={FiMessageCircle}
                >
                  <div className="space-y-3">
                    <p>
                      If you have any questions, concerns, or requests regarding
                      this Privacy Policy or your data, please don't hesitate to
                      contact us.
                    </p>

                    <div
                      className="p-4 rounded-lg"
                      style={{
                        backgroundColor: 'rgba(245, 158, 11, 0.08)',
                        border: '1px solid rgba(245, 158, 11, 0.3)',
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <FiAlertTriangle
                          className="w-5 h-5 mt-0.5 flex-shrink-0"
                          style={{ color: '#f59e0b' }}
                        />
                        <div>
                          <p
                            className="text-sm font-bold"
                            style={{ color: '#f59e0b' }}
                          >
                            📬 Important: Email Delivery Notice
                          </p>
                          <p
                            className="text-xs mt-1"
                            style={{ color: 'var(--muted)' }}
                          >
                            When you contact us, please be aware of the
                            following:
                          </p>
                          <ul
                            className="text-xs list-disc list-inside mt-2 space-y-1"
                            style={{ color: 'var(--muted)' }}
                          >
                            <li>
                              <strong>Check your spam folder:</strong> Our
                              response may go to your spam/junk folder. Please
                              check there if you don't hear back within 24
                              hours.
                            </li>
                            <li>
                              <strong>Add to contacts:</strong> Add{' '}
                              <span className="font-mono text-blue-500">
                                wdhejbul@gmail.com
                              </span>{' '}
                              to your contacts to ensure our emails go to your
                              inbox.
                            </li>
                            <li>
                              <strong>Provide valid information:</strong> We
                              cannot respond to invalid or fake email addresses.
                            </li>
                            <li>
                              <strong>Alternative contact:</strong> Provide your
                              phone number or WhatsApp if you want a direct
                              follow-up.
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div
                        className="p-4 rounded-xl"
                        style={{ backgroundColor: 'var(--border)' }}
                      >
                        <div className="flex items-center gap-3">
                          <FiMail
                            className="w-5 h-5"
                            style={{ color: 'var(--text)' }}
                          />
                          <div>
                            <p
                              className="text-xs"
                              style={{ color: 'var(--muted)' }}
                            >
                              Email
                            </p>
                            <p
                              className="text-sm font-medium"
                              style={{ color: 'var(--text)' }}
                            >
                              wdhejbul@gmail.com
                            </p>
                          </div>
                        </div>
                      </div>
                      <div
                        className="p-4 rounded-xl"
                        style={{ backgroundColor: 'var(--border)' }}
                      >
                        <div className="flex items-center gap-3">
                          <FiGlobe
                            className="w-5 h-5"
                            style={{ color: 'var(--text)' }}
                          />
                          <div>
                            <p
                              className="text-xs"
                              style={{ color: 'var(--muted)' }}
                            >
                              Website
                            </p>
                            <p
                              className="text-sm font-medium"
                              style={{ color: 'var(--text)' }}
                            >
                              wdhejbul.com
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      className="p-3 rounded-lg"
                      style={{
                        backgroundColor: 'rgba(16, 185, 129, 0.06)',
                        border: '1px solid rgba(16, 185, 129, 0.15)',
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <FiCheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <p
                          className="text-xs"
                          style={{ color: 'var(--muted)' }}
                        >
                          <span
                            className="font-medium"
                            style={{ color: 'var(--text)' }}
                          >
                            Response Guarantee:
                          </span>{' '}
                          We respond to all inquiries within 24 hours. If you
                          don't see our reply, please check your spam folder.
                        </p>
                      </div>
                    </div>
                  </div>
                </PolicySection>
              </motion.div>

              {/* Footer Note */}
              <motion.div
                variants={fadeInUp}
                className="mt-12 p-6 rounded-2xl text-center"
                style={{
                  backgroundColor: 'var(--border)',
                }}
              >
                <p className="text-sm" style={{ color: 'var(--muted)' }}>
                  This Privacy Policy is effective as of January 2025 and will
                  be updated as necessary. We encourage you to review this page
                  periodically for any changes.
                </p>
                <div className="flex flex-wrap justify-center gap-4 mt-4">
                  <Link
                    to="/terms"
                    className="text-sm transition-colors hover:underline"
                    style={{ color: 'var(--text)' }}
                  >
                    Terms & Conditions
                  </Link>
                  <span style={{ color: 'var(--muted)' }}>•</span>
                  <Link
                    to="/"
                    className="text-sm transition-colors hover:underline"
                    style={{ color: 'var(--text)' }}
                  >
                    Back to Home
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* Right Sidebar - 4 columns - Sticky */}
            <div className="lg:col-span-4">
              <div className="sticky" style={{ top: '80px' }}>
                <RightSidebar />
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%) rotate(12deg);
          }
          100% {
            transform: translateX(200%) rotate(12deg);
          }
        }
      `}</style>
    </>
  );
};

export default PrivacyPolicy;
